/**
 * M21 — Paginação do catálogo END global, independente de Cliente.
 * O cursor é o identificador estável do último registro da página anterior.
 */
import type { NdtAsset, NdtAssetType, NdtAssetStatus } from "./ndt-assets.js";

export interface NdtAssetListQuery {
  assetType?: NdtAssetType;
  status?: NdtAssetStatus;
  search?: string;
  cursor?: string | null;
  limit?: number;
}
export interface NdtAssetListPage {
  items: NdtAsset[];
  total: number;
  nextCursor: string | null;
}
export const DEFAULT_NDT_PAGE_SIZE = 25;
export const MAX_NDT_PAGE_SIZE = 100;

export function normalizeNdtAssetQuery(q: NdtAssetListQuery = {}): Required<Pick<NdtAssetListQuery, "limit">> & NdtAssetListQuery {
  const value = Number(q.limit ?? DEFAULT_NDT_PAGE_SIZE);
  if (!Number.isSafeInteger(value) || value < 1) throw new RangeError("Limite de página inválido.");
  const limit = Math.min(value, MAX_NDT_PAGE_SIZE);
  const search = (q.search ?? "").trim().slice(0, 120);
  const cursor = (q.cursor ?? "").trim();
  if (cursor.length > 256) throw new RangeError("Cursor de paginação inválido.");
  return { limit, ...(search ? { search } : {}), ...(cursor ? { cursor } : {}),
    ...(q.assetType ? { assetType: q.assetType } : {}),
    ...(q.status ? { status: q.status } : {}) };
}

export function paginateNdtAssets(assets: NdtAsset[], q: NdtAssetListQuery): NdtAssetListPage {
  const query = normalizeNdtAssetQuery(q);
  const needle = (query.search ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("pt-BR");
  const matches = assets.filter(a => !a.deletedAt &&
    (!query.assetType || a.assetType === query.assetType) &&
    (!query.status || a.status === query.status) &&
    (!needle || [a.internalCode, a.serialNumber, a.manufacturer, a.model, a.description]
      .join(" ").normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .toLocaleLowerCase("pt-BR").includes(needle))).sort((a,b) => a.id.localeCompare(b.id));
  const first = query.cursor ? matches.findIndex(a => a.id > query.cursor!) : 0;
  const after = first < 0 ? matches.length : first;
  const items = matches.slice(after, after + query.limit);
  return { items, total: matches.length,
    nextCursor: after + items.length < matches.length ? items[items.length - 1]?.id ?? null : null };
}
